import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Button, Select, MenuItem } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import CustomLabel from "../../components/CustomLabel";
import GenericFormDialog, { Field } from "../../components/GenericFormDialog";
import CustomDialog from "../../components/CustomDialog";
import CustomSelect from "../../components/CustomSelect";
import CustomTable from "../../components/table/CustomTable";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import CustomTextField from "../../components/CustomTextField";
import SearchHeaderContainer from "../../components/table/SearchHeaderContainer";
import Actions from "../../components/Actions";
import ConfirmDialog from "../../components/ConfirmDialog";
import { COLORS } from "../../assets/theme";
import CustomButton from "../../components/CustomButton";
import InvoiceItemsEditor, {
  InvoiceItem,
} from "../../components/invoices/InvoiceItemsEditor";
import { getAllBranches } from "../../services/BranchesService";
import {
  getAllInvoices,
  getAllAdminInvoices,
  createInvoice,
  updateInvoice,
  deleteInvoice,
  getInvoiceById,
} from "../../services/InvoicesService";
import { getUserId } from "../../services/authService";
import { getAllClients, getClientById } from "../../services/ClientsService";
import { getAllServices } from "../../services/ServicesService";

const pickNonEmptyString = (...values: unknown[]): string | undefined => {
  for (const value of values) {
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed) return trimmed;
    }
  }
  return undefined;
};

const toTitleCase = (value: string): string =>
  value
    .toLowerCase()
    .split(/\s+/)
    .map((word) => (word ? word[0].toUpperCase() + word.slice(1) : ""))
    .join(" ");

const STATUS_FORM_VALUE_MAP: Record<
  1 | 2 | 3,
  "paid" | "unpaid" | "cancelled"
> = {
  1: "paid",
  2: "unpaid",
  3: "cancelled",
};

const STATUS_LABEL_MAP: Record<1 | 2 | 3, string> = {
  1: "Paid",
  2: "Unpaid",
  3: "Cancelled",
};

const PAYMENT_METHOD_LABEL_MAP: Record<0 | 1 | 2 | 3 | 4, string> = {
  0: "Cash",
  1: "Card",
  2: "Cash",
  3: "Bank Transfer",
  4: "Online Payment",
};

const resolveStatusLabel = (entry: any): string => {
  const invoice = entry?.invoice ?? {};
  const statusId = Number(
    entry?.status_id ??
      invoice?.status_id ??
      invoice?.status?.id ??
      entry?.status?.id
  );
  if (Number.isFinite(statusId)) {
    const label = STATUS_LABEL_MAP[statusId as 1 | 2 | 3];
    if (label) {
      return label;
    }
  }

  const statusString = pickNonEmptyString(
    entry?.status,
    invoice?.status,
    invoice?.status_name,
    invoice?.status_label
  );

  if (statusString) {
    return toTitleCase(statusString);
  }

  if (typeof entry?.status_id === "string") {
    return toTitleCase(entry.status_id);
  }

  return "Unpaid";
};

const resolveStatusFormValue = (
  status: unknown,
  fallbackId?: unknown
): "paid" | "unpaid" | "cancelled" => {
  const numeric = Number(status ?? fallbackId);
  if (Number.isFinite(numeric)) {
    const formValue = STATUS_FORM_VALUE_MAP[numeric as 1 | 2 | 3];
    if (formValue) {
      return formValue;
    }
  }

  if (typeof status === "string" && status.trim()) {
    const normalized = status.trim().toLowerCase();
    if (
      normalized === "paid" ||
      normalized === "unpaid" ||
      normalized === "cancelled"
    ) {
      return normalized;
    }
  }

  if (typeof fallbackId === "string" && fallbackId.trim()) {
    const normalized = fallbackId.trim().toLowerCase();
    if (
      normalized === "paid" ||
      normalized === "unpaid" ||
      normalized === "cancelled"
    ) {
      return normalized as "paid" | "unpaid" | "cancelled";
    }
  }

  return "unpaid";
};

const resolvePaymentMethodLabel = (entry: any): string => {
  const invoice = entry?.invoice ?? {};
  const methodId = Number(
    entry?.payment_method_id ??
      invoice?.payment_method_id ??
      invoice?.payment_method?.id
  );
  if (Number.isFinite(methodId)) {
    const label = PAYMENT_METHOD_LABEL_MAP[methodId as 0 | 1 | 2 | 3 | 4];
    if (label) {
      return label;
    }
  }

  if (typeof entry?.payment_method_id === "string") {
    const parsed = Number(entry.payment_method_id);
    if (Number.isFinite(parsed)) {
      const label = PAYMENT_METHOD_LABEL_MAP[parsed as 0 | 1 | 2 | 3 | 4];
      if (label) {
        return label;
      }
    }
    return toTitleCase(entry.payment_method_id);
  }

  return "Cash";
};

type InvoiceClientOption = {
  id: string;
  name: string;
  email?: string;
  mobile?: string;
};

const Invoices: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const venueId = params.branchId;
  const [editOpen, setEditOpen] = React.useState(false);
  const [editingRow, setEditingRow] = React.useState<any | null>(null);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [toDeleteId, setToDeleteId] = React.useState<string | number | null>(
    null
  );
  const [selectValue, setSelectValue] = React.useState<
    string | number | (string | number)[]
  >("");
  const [activeBranch, setActiveBranch] = React.useState<string | null>(null);
  const [branchList, setBranchList] = React.useState<string[]>([]);

  const [branches, setBranches] = React.useState<any[]>([]);
  const [invoices, setInvoices] = React.useState<any[]>([]);
  const [services, setServices] = React.useState<any[]>([]);
  const [clients, setClients] = React.useState<InvoiceClientOption[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [search, setSearch] = React.useState<string>("");
  const [dateRange, setDateRange] = React.useState<
    { start?: string; end?: string } | [string?, string?] | null
  >(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    return { start: start.toISOString(), end: end.toISOString() };
  });
  const [statusFilter, setStatusFilter] = React.useState<string>("all");

  React.useEffect(() => {
    const fetchBranches = async () => {
      try {
        if (!venueId) {
          setBranches([]);
          return;
        }

        const response = await getAllBranches(venueId);

        let branchesData = [];
        if (response && response.businesses) {
          branchesData = response.businesses;
        } else if (response && response.data) {
          branchesData = response.data;
        } else if (Array.isArray(response)) {
          branchesData = response;
        }

        console.log("Processed branches data:", branchesData);
        setBranches(Array.isArray(branchesData) ? branchesData : []);
      } catch (error) {
        console.error("Error fetching branches:", error);
        setBranches([]);
      }
    };

    if (venueId) {
      fetchBranches();
    } else {
      console.log("No venueId available, skipping branch fetch");
    }
  }, [venueId]);

  const fetchInvoices = React.useCallback(async () => {
    const isAllSelection = selectValue === "all";
    const businessId = !isAllSelection ? selectValue || venueId : venueId;

    if (isAllSelection && !venueId) {
      console.warn("No venueId available, skipping admin invoices fetch");
      setInvoices([]);
      return;
    }

    if (!businessId && !isAllSelection) {
      setInvoices([]);
      return;
    }

    setLoading(true);
    try {
      const response = isAllSelection
        ? await getAllAdminInvoices(venueId ?? null)
        : await getAllInvoices(String(businessId));
      console.log("Invoices API response:", response);
      const invoicesData =
        response?.data?.invoices ||
        response?.invoices ||
        (Array.isArray(response) ? response : []);
      const normalizedInvoices = (
        Array.isArray(invoicesData) ? invoicesData : []
      ).map((item: any) => {
        const invoice = item?.invoice ?? {};
        const statusIdCandidate =
          item?.status_id ??
          invoice?.status_id ??
          invoice?.status?.id ??
          item?.status?.id;
        const paymentMethodIdCandidate =
          item?.payment_method_id ??
          invoice?.payment_method_id ??
          invoice?.payment_method?.id;

        const statusIdNumeric = Number(statusIdCandidate);
        const paymentMethodIdNumeric = Number(paymentMethodIdCandidate);
        const enriched = {
          ...item,
          status_id: Number.isFinite(statusIdNumeric)
            ? statusIdNumeric
            : statusIdCandidate ?? item?.status_id,
          payment_method_id: Number.isFinite(paymentMethodIdNumeric)
            ? paymentMethodIdNumeric
            : paymentMethodIdCandidate ?? item?.payment_method_id,
        };

        const businessId =
          item.business_id || item.businessId || enriched.business_id;
        const branchInfo = branches.find(
          (b: any) => String(b.business_id) === String(businessId)
        );
        const branchName = branchInfo ? branchInfo.name : "Unknown Branch";

        return {
          ...enriched,
          status: resolveStatusLabel(enriched),
          method: resolvePaymentMethodLabel(enriched),
          branch: branchName,
          services: (() => {
            try {
              const details =
                item.details || item.invoice?.details || item.services || [];
              const arr = Array.isArray(details) ? details : [details];
              const names = arr
                .map((d: any) => {
                  if (!d) return null;
                  if (typeof d === "string" || typeof d === "number") {
                    const svc = services.find(
                      (s: any) => String(s.id) === String(d)
                    );
                    return svc?.title || svc?.name || String(d);
                  }
                  return (
                    d.title ||
                    d.service ||
                    d.name ||
                    d.service_name ||
                    d.service_id ||
                    null
                  );
                })
                .filter(Boolean);
              return names.join(", ");
            } catch (e) {
              return "";
            }
          })(),
        };
      });
      setInvoices(normalizedInvoices);
    } catch (error) {
      console.error("Error fetching invoices:", error);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [selectValue, venueId, services]);

  React.useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  React.useEffect(() => {
    const fetchServices = async () => {
      const businessId = selectValue || venueId;
      if (!businessId) return;
      try {
        const response = await getAllServices(String(businessId));
        console.log("Services API response:", response);
        const servicesData =
          response?.data?.services ||
          response?.services ||
          (Array.isArray(response) ? response : []);
        setServices(Array.isArray(servicesData) ? servicesData : []);
      } catch (error) {
        console.error("Error fetching services:", error);
        setServices([]);
      }
    };
    fetchServices();
  }, [selectValue, venueId]);

  React.useEffect(() => {
    const fetchClients = async () => {
      const isAllSelection = selectValue === "all";
      const businessId = isAllSelection ? "all" : selectValue || venueId;

      if (!businessId) {
        setClients([]);
        return;
      }

      try {
        const response = await getAllClients(String(businessId), venueId);

        let clientsData: any[] = [];
        if (response?.users) {
          clientsData = response.users;
        } else if (response?.clients) {
          clientsData = response.clients;
        } else if (response?.data?.clients) {
          clientsData = response.data.clients;
        } else if (response?.data?.users) {
          clientsData = response.data.users;
        } else if (Array.isArray(response?.data)) {
          clientsData = response.data;
        } else if (Array.isArray(response)) {
          clientsData = response;
        }

        const mappedClients: InvoiceClientOption[] = (
          Array.isArray(clientsData) ? clientsData : []
        )
          .map((c: any) => {
            const id =
              c?.value ??
              c?.id ??
              c?.client_id ??
              c?._id ??
              c?.user_id ??
              c?.clientId;
            if (!id) return null;
            const name =
              c?.label ??
              c?.name ??
              c?.fullName ??
              c?.full_name ??
              c?.client_name ??
              c?.email ??
              `Client ${id}`;
            const email = c?.email ?? c?.client_email ?? c?.user?.email ?? "";
            const mobile =
              c?.mobile ??
              c?.phone ??
              c?.phone_number ??
              c?.contact_number ??
              c?.user?.mobile ??
              "";
            return {
              id: String(id),
              name: String(name),
              email,
              mobile,
            } as InvoiceClientOption;
          })
          .filter(Boolean) as any[];

        setClients(mappedClients);
      } catch (error) {
        console.error("Error fetching clients:", error);
        setClients([]);
      }
    };

    fetchClients();
  }, [selectValue, venueId]);

  const formFields: Field[] = [
    {
      name: "clientName",
      label: "Client Name",
      colSpan: 1,
      customRender: (value: string, onChange: (v: string) => void, helpers) => {
        const invoiceClients = invoices
          .map((inv: any) =>
            inv.client_id && inv.client_name
              ? {
                  id: inv.client_id,
                  name:
                    inv.client_name ||
                    inv.client?.name ||
                    inv.client?.full_name ||
                    inv.client?.fullName,
                  email: inv.client?.email || inv.email,
                  mobile: inv.client?.mobile || inv.mobile,
                }
              : null
          )
          .filter(Boolean) as any[];

        const mergedClients = [
          ...(Array.isArray(clients) ? clients : []),
          ...invoiceClients,
        ].reduce((acc, c: any) => {
          const id =
            c?.id ??
            c?.value ??
            c?.client_id ??
            c?._id ??
            c?.user_id ??
            c?.clientId;
          if (!id) return acc;
          const key = String(id);
          if (!acc.has(key)) {
              acc.set(key, {
                id: key,
                name:
                  c?.name ||
                  c?.label ||
                c?.client_name ||
                c?.fullName ||
                c?.full_name ||
                c?.email ||
                `Client ${key}`,
              email: c?.email || "",
                mobile:
                  c?.mobile ||
                  c?.phone ||
                  c?.phoneNumber ||
                  c?.mobileNumber ||
                  "",
              });
            }
            return acc;
        }, new Map<string, InvoiceClientOption>());

        const clientList: InvoiceClientOption[] = Array.from(
          mergedClients.values()
        );

        const options = clientList.map((client: any) => ({
          label: client.name,
          value: client.id,
        }));
        return (
          <>
            <CustomLabel
              sx={{ p: 0.1 }}
              text={"Client Name"}
              color={COLORS.typographyColor}
              fontWeight={500}
              fontSize={14}
            />
            <CustomSelect
              label="Client Name"
              value={value || ""}
              options={options}
              onChange={(v: any) => {
                onChange(String(v || ""));
                if (v) {
                  const selected = clientList.find(
                    (c) => String(c.id) === String(v)
                  );
                  if (selected) {
                    helpers?.setFieldValue("email", selected.email || "");
                    helpers?.setFieldValue(
                      "phoneNumber",
                      selected.mobile || ""
                    );
                    return;
                  }
                  getClientById(String(v))
                    .then((client) => {
                      if (client && client.user) {
                        helpers?.setFieldValue(
                          "email",
                          client.user.email || ""
                        );
                        helpers?.setFieldValue(
                          "phoneNumber",
                          client.user.mobile || ""
                        );
                      }
                    })
                    .catch(console.error);
                }
              }}
              fullWidth
              placeholder="Select..."
            />
          </>
        );
      },
    },
    { name: "email", label: "Email" },
    { name: "phoneNumber", label: "Phone Number" },
    { name: "invoice_date", label: "Date", type: "date" },
    {
      name: "status",
      label: "Status",
      type: "dropdown",
      options: ["paid", "cancelled", "unpaid"],
    },
    {
      name: "paymentMethod",
      label: "Payment method",
      type: "dropdown",
      options: ["cash", "card", "bank transfer"],
    },
    { name: "notes", label: "Notes", colSpan: 2 },
    {
      name: "items",
      label: "Items",
      colSpan: 2,
      customRender: (_value: any, onChange: (v: any) => void, helpers) => (
        <InvoiceItemsEditor
          value={_value}
          onChange={onChange}
          vatValue={helpers?.values?.vat || 0}
          onVatChange={(v) => helpers?.setFieldValue("vat", v)}
          discountValue={helpers?.values?.discount || 0}
          onDiscountChange={(v) => helpers?.setFieldValue("discount", v)}
          isEditMode={!!editingRow}
          servicesList={services}
        />
      ),
    },
  ];

  const openEditForId = async (id: string | number) => {
    try {
      const response = await getInvoiceById(String(id));
      const invoiceData = response?.data || response;

      const rowBusinessId =
        invoiceData.business_id ||
        invoiceData.invoice?.business_id ||
        invoiceData.businessId;

      if (rowBusinessId) {
        try {
          const servicesResponse = await getAllServices(String(rowBusinessId));
          const servicesData =
            servicesResponse?.data?.services ||
            servicesResponse?.services ||
            (Array.isArray(servicesResponse) ? servicesResponse : []);
          setServices(Array.isArray(servicesData) ? servicesData : []);
          console.log("Services fetched for invoice edit:", servicesData);
        } catch (error) {
          console.error("Error fetching services for business:", error);
          setServices([]);
        }
      }

      setEditingRow(invoiceData);
      setEditOpen(true);
    } catch (error) {
      console.error("Error fetching invoice:", error);
    }
  };

  const wiredActions = Actions({
    handleOpenEditModal: { handler: openEditForId },
    onDelete: {
      handler: async (id: number | string) => {
        setToDeleteId(id);
        setConfirmOpen(true);
      },
    },
    onPdf: {
      handler: async (id: number | string) => {
        const branchId = params.branchId;
        navigate(`/dashboard/${branchId}/invoicepdf/${id}`);
      },
    },
  });

  const [rows, setRows] = React.useState(() => {
    const total = 10;
    return Array.from({ length: total }, (_, i) => {
      const id = i + 1;
      const start = new Date();
      start.setDate(start.getDate() + i);
      const end = new Date(start.getTime() + 60 * 60000);
      return {
        id,
        bookingSource: id % 2 === 0 ? "Website" : "App",
        createdBy: `User ${((id - 1) % 5) + 1}`,
        phoneNumber: `+1-202-555-${String(1000 + id).slice(-4)}`,
        priority: id % 3 === 0 ? "High" : "Normal",
        status: id % 3 === 0 ? "paid" : id % 3 === 1 ? "unpaid" : "Cancelled",
        date: start.toISOString(),
        branch: `Branch ${((id - 1) % 3) + 1}`,
        startTime: start.toTimeString().slice(0, 5),
        endTime: end.toTimeString().slice(0, 5),
      };
    });
  });

  React.useEffect(() => {
    const b = Array.from(new Set(rows.map((r: any) => r.branch)));
    setBranchList(b.sort());
  }, [rows]);

  React.useEffect(() => {
    if (branches.length > 0 && !selectValue) {
      setSelectValue("all");
    }
  }, [branches, selectValue]);

  const [invoiceItems, setInvoiceItems] = React.useState<InvoiceItem[]>([]);

  const [tableDialogOpen, setTableDialogOpen] = React.useState(false);

  const exportCsv = React.useCallback(() => {
    if (!invoices || invoices.length === 0) return;
    const keys = [
      "Invoice_number",
      "client_name",
      "mobile",
      "invoice_date",
      "branch",
      "status",
      "method",
      "total_amount",
      "net_amount",
      "notes",
      "services",
    ];
    const header = keys.join(",");
    const lines = invoices.map((r: any) =>
      keys
        .map((k) => {
          const v = r[k] ?? "";
          const s = String(v).replace(/"/g, '""');
          return `"${s}"`;
        })
        .join(",")
    );
    const csv = [header, ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `invoices-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [invoices]);

  return (
    <Box sx={{ width: "100%", maxWidth: "90vw", overflowX: "hidden" }}>
      <Box sx={{ mt: { xs: 2, sm: 3 } }}>
        <CustomLabel
          text="Invoices"
          variant="h4"
          fontWeight="semi-bold"
          sx={{ mb: 2 }}
        />
        <SearchHeaderContainer
          search={search}
          setSearch={setSearch}
          dateRangeConfig={{
            value: dateRange ?? undefined,
            onChange: (v: any) => setDateRange(v),
            fullWidth: false,
          }}
          selectConfig={{
            options: [
              { label: "All", value: "all" },
              ...branches.map((branch) => ({
                label: branch.name || "Unknown Branch",
                value: branch.business_id,
              })),
            ],
            value: selectValue,
            onChange: (v: any) => {
              setSelectValue(v);
            },
            placeholder: "Select branch",
            label: "Select Branch",
            size: "small",
            searchable: false,
            clearable: true,
            multiSelect: false,
            sx: {
              backgroundColor: COLORS.WhiteBackground,
            },
          }}
          selectConfig2={{
            options: [
              { label: "All", value: "all" },
              { label: "Paid", value: "Paid" },
              { label: "Unpaid", value: "Unpaid" },
              { label: "Cancelled", value: "Cancelled" },
            ],
            value: statusFilter,
            onChange: (v: any) => setStatusFilter(v),
            placeholder: "Status",
            label: "Status",
            size: "small",
            searchable: false,
            clearable: true,
            sx: { width: 180 },
          }}
          buttonConfig={[
            {
              size: "small",
              bgColor: COLORS.MainColor,
              startIcon: <AddIcon />,
              onClick: () => {
                setEditingRow(null);
                setEditOpen(true);
              },
              textColor: COLORS.WhiteText,
              minHeight: 36,
              maxHeight: 36,
              sx: {
                width: { xs: "96%", sm: 36 },
                minWidth: { sm: 36 },
                marginRight: { xs: 1.5, sm: 0 },
              },
            },
            {
              label: "Export CSV",
              onClick: () => Promise.resolve(exportCsv()),
              startIcon: <FileDownloadIcon />,
              variant: "contained",
              bgColor: COLORS.MainColor,
              textColor: "#fff",
              showExportProgress: true,
              sx: {
                width: { xs: "96%", sm: "auto" },
                marginRight: { xs: 1.5, sm: 0 },
              },
            },
          ]}
        />

        <CustomTable<any>
          filters={{ page: 0, size: 10, search, branch: activeBranch }}
          clientSidePagination={true}
          loading={loading}
          fields={[
            { key: "Invoice_number", header: "Invoice #" },
            { key: "client_name", header: "Client Name" },
            { key: "mobile", header: "Phone Number" },
            { key: "invoice_date", header: "Date" },
            {
              key: "updated_at",
              header: "Created At",
              transform: (r: any) =>
                r?.invoice?.updated_at ??
                r?.updated_at ??
                r?.invoice?.created_at ??
                r?.created_at ??
                "",
            },
            { key: "branch", header: "Branch" },
            { key: "status", header: "Status" },
            { key: "method", header: "Payment Method" },
            { key: "services", header: "Services" },
            { key: "net_amount", header: "Net Amount" },
            { key: "notes", header: "Notes" },
          ]}
          actions={wiredActions}
          fetchFunc={async (params?: any) => {
            const q = (params && params.search) || search || "";
            const normalizedQuery = String(q || "")
              .toLowerCase()
              .trim();

            const filtered = invoices.filter((r: any) => {
              if (dateRange) {
                const asDate = (iso?: string) => {
                  if (!iso) return null;
                  const d = new Date(iso);
                  return Number.isNaN(d.getTime()) ? null : d;
                };

                const rowDate = asDate(r.created_at || r.invoice_date);
                if (rowDate) {
                  let startDate: Date | null = null;
                  let endDate: Date | null = null;
                  if (Array.isArray(dateRange)) {
                    startDate = asDate(dateRange[0] as any);
                    endDate = asDate(dateRange[1] as any);
                  } else {
                    startDate = asDate((dateRange as any).start);
                    endDate = asDate((dateRange as any).end);
                  }

                  if (endDate) {
                    endDate.setHours(23, 59, 59, 999);
                  }

                  if (startDate && rowDate < startDate) return false;
                  if (endDate && rowDate > endDate) return false;
                }
              }

              if (statusFilter && statusFilter !== "all") {
                const rowStatus = r.status;
                if (rowStatus !== statusFilter) return false;
              }

              if (!normalizedQuery) return true;

              const haystack = [
                r.Invoice_number,
                r.client_name,
                r.mobile,
                r.branch,
                r.status,
                r.method,
                r.total_amount,
                r.net_amount,
                r.notes,
                r.services,
              ]
                .filter(Boolean)
                .map((v: any) => String(v).toLowerCase())
                .join(" ");

              return haystack.includes(normalizedQuery);
            });

            const content = filtered;
            const totalElements = content.length;
            const pageSize = (params && params.size) || 10;
            const totalPages = Math.max(1, Math.ceil(totalElements / pageSize));

            return {
              content,
              number: 0,
              size: pageSize,
              totalElements,
              totalPages,
            };
          }}
        />
        <Box
          sx={{
            backgroundColor: COLORS.WhiteBackground,
            borderRadius: "0 0 10px 10px",
            padding: "16px",
            marginTop: "-10px",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 2,
            mb: 3,
          }}
        >
          <CustomLabel
            text="Total Net Amount:"
            sx={{ color: COLORS.typographyColor }}
          />
          <CustomLabel
            text={new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(
              invoices
                .filter((r: any) => {
                  if (dateRange) {
                    const asDate = (iso?: string) => {
                      if (!iso) return null;
                      const d = new Date(iso);
                      return Number.isNaN(d.getTime()) ? null : d;
                    };

                    const rowDate = asDate(r.created_at || r.invoice_date);
                    if (rowDate) {
                      let startDate: Date | null = null;
                      let endDate: Date | null = null;
                      if (Array.isArray(dateRange)) {
                        startDate = asDate(dateRange[0] as any);
                        endDate = asDate(dateRange[1] as any);
                      } else {
                        startDate = asDate((dateRange as any).start);
                        endDate = asDate((dateRange as any).end);
                      }

                      if (endDate) {
                        endDate.setHours(23, 59, 59, 999);
                      }

                      if (startDate && rowDate < startDate) return false;
                      if (endDate && rowDate > endDate) return false;
                    }
                  }
                  return true;
                })
                .filter((r: any) => {
                  if (selectValue && selectValue !== "all") {
                    const itemBusinessId =
                      r.business_id ?? r.businessId ?? null;
                    return String(itemBusinessId) === String(selectValue);
                  }
                  return true;
                })
                .filter((r: any) => {
                  if (!search) return true;
                  const normalizedQuery = String(search || "")
                    .toLowerCase()
                    .trim();
                  const haystack = [
                    r.Invoice_number,
                    r.client_name,
                    r.mobile,
                    r.branch,
                    r.status,
                    r.method,
                    r.total_amount,
                    r.net_amount,
                    r.notes,
                    r.services,
                  ]
                    .filter(Boolean)
                    .map((v: any) => String(v).toLowerCase())
                    .join(" ");
                  return haystack.includes(normalizedQuery);
                })
                .filter((r: any) => {
                  const statusId = Number(
                    r.status_id ??
                      r.invoice?.status_id ??
                      r.invoice?.status?.id ??
                      r.status?.id
                  );
                  return statusId === 1;
                })
                .reduce((sum: number, r: any) => {
                  const netAmount = Number(r.net_amount) || 0;
                  return sum + netAmount;
                }, 0)
            )}
            fontWeight="bold"
            sx={{ color: COLORS.MainColor }}
          />
        </Box>
        <GenericFormDialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          title={editingRow ? `Edit Invoice` : "Add Invoice"}
          fields={editingRow ? formFields : [...formFields]}
          initialValues={
            editingRow
              ? {
                  clientName:
                  editingRow.invoice?.client_id || editingRow.client_id,
                  email: editingRow.client?.email || "",
                  phoneNumber: editingRow.client?.mobile || "",
                  invoice_date: (() => {
                    const date =
                      editingRow.invoice?.created_at ||
                      editingRow.created_at ||
                      editingRow.invoice?.invoice_date ||
                      editingRow.invoice_date;
                    if (!date) return "";
                    const dateObj = new Date(date);
                    return dateObj.toISOString().split("T")[0];
                  })(),
                  status: resolveStatusFormValue(
                    pickNonEmptyString(
                      editingRow.status,
                      editingRow.invoice?.status,
                      editingRow.invoice?.status_name,
                      editingRow.invoice?.status_label
                    ),
                    editingRow.invoice?.status_id ??
                      editingRow.status_id ??
                      editingRow.invoice?.status?.id ??
                      editingRow.status?.id
                  ),
                  paymentMethod:
                    Number(
                      editingRow.invoice?.payment_method_id ||
                        editingRow.payment_method_id
                    ) === 1
                      ? "card"
                      : Number(
                          editingRow.invoice?.payment_method_id ||
                            editingRow.payment_method_id
                        ) === 2
                      ? "cash"
                      : "bank transfer",
                  notes: editingRow.invoice?.notes || editingRow.notes || "",
                    items: editingRow.details
                      ? editingRow.details.map((s: any, index: number) => {
                        const detailServiceId =
                          s.service_id ?? s.serviceId ?? s.serviceID;
                        const normalizedServiceId =
                          detailServiceId !== undefined &&
                          detailServiceId !== null
                            ? String(detailServiceId)
                            : undefined;
                        let matchedService = services.find(
                          (srv) =>
                            String(
                              srv.id || srv._id || srv.service_id || ""
                            ) === String(normalizedServiceId || "")
                        );

                        if (!matchedService && s.title) {
                          matchedService = services.find((srv) => {
                            const serviceNumber = srv.title
                              ? String(srv.title).match(/^\d+/)?.[0]
                              : null;
                            const detailTitle = String(s.title).trim();
                            return (
                              serviceNumber === detailTitle ||
                              srv.title === detailTitle
                            );
                          });
                        }

                        const matchedServiceId =
                          matchedService?.id ||
                          matchedService?._id ||
                          matchedService?.service_id;
                        const resolvedServiceId =
                          matchedServiceId ||
                          normalizedServiceId ||
                          undefined;

                        let serviceValue;
                        if (resolvedServiceId) {
                          serviceValue = String(resolvedServiceId);
                        } else if (matchedService && matchedService.title) {
                          const titleMatch = matchedService.title.match(/^\d+/);
                          if (titleMatch) {
                            serviceValue = parseInt(titleMatch[0]);
                          } else {
                            serviceValue = matchedService.title;
                          }
                        } else if (s.title) {
                          const parsedTitle = parseInt(s.title);
                          serviceValue = !isNaN(parsedTitle)
                            ? parsedTitle
                            : s.title;
                        } else {
                          serviceValue = s.service_id || 60;
                        }

                        console.log("Mapping service:", {
                          service_id: s.service_id,
                          detail_title: s.title,
                          matchedService: matchedService,
                          serviceValue: serviceValue,
                        });

                        return {
                          id: index + 1,
                          service: serviceValue,
                          serviceId: resolvedServiceId,
                          quantity: s.quantity,
                          unitCost: s.amount,
                          discount: s.discount,
                          totalPrice: s.net_amount,
                        };
                      })
                    : [],
                  invoice_number:
                    editingRow.invoice?.Invoice_number ||
                    editingRow.invoice?.invoice_number ||
                    editingRow.Invoice_number ||
                    editingRow.invoice_number,
                  appointment_id:
                    editingRow.invoice?.appointment_id ||
                    editingRow.appointment_id,
                  net_amount:
                    editingRow.invoice?.net_amount || editingRow.net_amount,
                  total_amount:
                    editingRow.invoice?.total_amount || editingRow.total_amount,
                  vat: String(editingRow.invoice?.Vat || editingRow.Vat || 0),
                  discount: String(
                    editingRow.invoice?.discount || editingRow.discount || 0
                  ),
                }
              : { vat: "0", discount: "0", paymentMethod: "cash" }
          }
          onSubmit={async (values) => {
            try {
              const services = (
                Array.isArray(values.items) ? values.items : []
              ).map((item: any) => ({
                service_id: item.serviceId || String(item.service),
                quantity: item.quantity,
                amount: item.unitCost,
                discount: item.discount,
                net_amount: item.totalPrice,
              }));

              const totalAmount = services.reduce(
                (sum: number, item: any) => sum + Number(item.net_amount || 0),
                0
              );
              const discountPercent = Number(values.discount) || 0;
              const discountAmount =
                Math.round(((totalAmount * discountPercent) / 100) * 100) / 100;
              const amountAfterDiscount = Math.max(
                0,
                totalAmount - discountAmount
              );
              const vatPercent = Number(values.vat) || 0;
              const vatAmount =
                Math.round(((amountAfterDiscount * vatPercent) / 100) * 100) /
                100;
              const netAmount =
                Math.round((amountAfterDiscount + vatAmount) * 100) / 100;

              let businessIdToUse;
              if (editingRow && selectValue === "all") {
                businessIdToUse =
                  editingRow.business_id ||
                  editingRow.invoice?.business_id ||
                  editingRow.businessId;
              } else {
                businessIdToUse = selectValue || venueId;
              }

              const payload = {
                client_id: values.clientName,
                appointment_id: values.appointment_id || "1",
                business_id: businessIdToUse,
                status_id:
                  values.status === "paid"
                    ? 1
                    : values.status === "unpaid"
                    ? 2
                    : 3,
                invoice_number: values.invoice_number || 5967,
                total_amount: totalAmount,
                discount: discountPercent,
                payment_method_id:
                  values.paymentMethod === "card"
                    ? 1
                    : values.paymentMethod === "cash"
                    ? 2
                    : 3,
                created_by: getUserId() || "1",
                created_at: new Date().toISOString(),
                Vat: vatPercent,
                net_amount: String(netAmount),
                services,
                notes: values.notes,
                invoice_date: values.invoice_date,
                id: editingRow ? String(editingRow.invoice.id) : undefined,
              };

              if (editingRow) {
                await updateInvoice(String(editingRow.invoice.id), payload);
              } else {
                await createInvoice(payload);
              }
              await fetchInvoices();
              setEditOpen(false);
              setEditingRow(null);
            } catch (error) {
              console.error("Error saving invoice:", error);
            }
          }}
        />
        <CustomDialog
          open={tableDialogOpen}
          onClose={() => setTableDialogOpen(false)}
          title="Invoice Line Items"
        >
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
            <CustomButton
              label="add"
              iconButtonSize={40}
              startIcon={<AddIcon />}
              textColor={COLORS.WhiteText}
              onClick={() => {
                setInvoiceItems((items) => [
                  ...items,
                  {
                    id: items.length
                      ? Math.max(...items.map((it) => it.id)) + 1
                      : 1,
                    service: 30,
                    quantity: 1,
                    unitCost: 0,
                    discount: 0,
                    totalPrice: 0,
                  },
                ]);
              }}
            />
          </Box>
          <TableContainer
            component={Paper}
            sx={{
              mt: 2,
              width: "100%",
              overflowX: { xs: "auto", md: "visible" },
            }}
          >
            <Table size="small" sx={{ minWidth: 800, width: "100%" }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ px: 0.5, py: 0.5, fontSize: 13 }}>
                    Service
                  </TableCell>
                  <TableCell sx={{ px: 0.5, py: 0.5, fontSize: 13 }}>
                    Quantity
                  </TableCell>
                  <TableCell sx={{ px: 0.5, py: 0.5, fontSize: 13 }}>
                    Unit Cost
                  </TableCell>
                  <TableCell sx={{ px: 0.5, py: 0.5, fontSize: 13 }}>
                    Discount (%)
                  </TableCell>
                  <TableCell sx={{ px: 0.5, py: 0.5, fontSize: 13 }}>
                    Total Price
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoiceItems.map((item, idx) => (
                  <TableRow key={item.id}>
                    <TableCell sx={{ px: 0.5, py: 0.5 }}>
                      <Select
                        size="small"
                        value={item.service}
                        onChange={(e) => {
                          const newService = Number(e.target.value);
                          setInvoiceItems((items) =>
                            items.map((it, i) => {
                              if (i !== idx) return it;
                              const updated: InvoiceItem = {
                                ...it,
                                service: newService,
                              };
                              if (newService === 60) {
                                updated.quantity = 1;
                                updated.unitCost = 20;
                                updated.discount = 0;
                              }
                              if (newService === 90) {
                                updated.quantity = 1;
                                updated.unitCost = 30;
                                updated.discount = 0;
                              }
                              if (newService === 120) {
                                updated.quantity = 1;
                                updated.unitCost = 40;
                                updated.discount = 0;
                              }
                              const qty = Number(updated.quantity || 0);
                              const unit = Number(updated.unitCost || 0);
                              const subtotal = qty * unit;
                              const discountPercent = Number(
                                updated.discount || 0
                              );
                              const discountAmount =
                                (subtotal * discountPercent) / 100;
                              updated.totalPrice =
                                Math.round((subtotal - discountAmount) * 100) /
                                100;
                              return updated;
                            })
                          );
                        }}
                        sx={{ minWidth: 60 }}
                      >
                        <MenuItem value={60}></MenuItem>
                        <MenuItem value={90}></MenuItem>
                        <MenuItem value={120}></MenuItem>
                      </Select>
                    </TableCell>
                    <TableCell sx={{ px: 0.5, py: 0.5 }}>
                      <CustomTextField
                        value={item.quantity}
                        type="number"
                        onChange={(e) => {
                          const value = Number(e.target.value);
                          setInvoiceItems((items) =>
                            items.map((it, i) => {
                              if (i !== idx) return it;
                              const updated = {
                                ...it,
                                quantity: value,
                              } as InvoiceItem;
                              const qty = Number(updated.quantity || 0);
                              const unit = Number(updated.unitCost || 0);
                              const subtotal = qty * unit;
                              const discountPercent = Number(
                                updated.discount || 0
                              );
                              const discountAmount =
                                (subtotal * discountPercent) / 100;
                              updated.totalPrice =
                                Math.round((subtotal - discountAmount) * 100) /
                                100;
                              return updated;
                            })
                          );
                        }}
                        fullWidth
                        size="small"
                        sx={{ fontSize: 13, px: 0.5, py: 0.5 }}
                      />
                    </TableCell>
                    <TableCell sx={{ px: 0.5, py: 0.5 }}>
                      <CustomTextField
                        value={item.unitCost}
                        type="number"
                        onChange={(e) => {
                          const value = Number(e.target.value);
                          setInvoiceItems((items) =>
                            items.map((it, i) => {
                              if (i !== idx) return it;
                              const updated = {
                                ...it,
                                unitCost: value,
                              } as InvoiceItem;
                              const qty = Number(updated.quantity || 0);
                              const unit = Number(updated.unitCost || 0);
                              const subtotal = qty * unit;
                              const discountPercent = Number(
                                updated.discount || 0
                              );
                              const discountAmount =
                                (subtotal * discountPercent) / 100;
                              updated.totalPrice =
                                Math.round((subtotal - discountAmount) * 100) /
                                100;
                              return updated;
                            })
                          );
                        }}
                        fullWidth
                        size="small"
                        sx={{ fontSize: 13, px: 0.5, py: 0.5 }}
                      />
                    </TableCell>
                    <TableCell sx={{ px: 0.5, py: 0.5 }}>
                      <CustomTextField
                        value={item.discount}
                        type="number"
                        onChange={(e) => {
                          const value = Number(e.target.value);
                          setInvoiceItems((items) =>
                            items.map((it, i) => {
                              if (i !== idx) return it;
                              const updated = {
                                ...it,
                                discount: Math.min(100, Math.max(0, value)),
                              } as InvoiceItem;
                              const qty = Number(updated.quantity || 0);
                              const unit = Number(updated.unitCost || 0);
                              const subtotal = qty * unit;
                              const discountPercent = Number(
                                updated.discount || 0
                              );
                              const discountAmount =
                                (subtotal * discountPercent) / 100;
                              updated.totalPrice =
                                Math.round((subtotal - discountAmount) * 100) /
                                100;
                              return updated;
                            })
                          );
                        }}
                        fullWidth
                        size="small"
                        sx={{ fontSize: 13, px: 0.5, py: 0.5 }}
                        inputProps={{ step: 0.01, min: 0, max: 100 }}
                      />
                    </TableCell>
                    <TableCell sx={{ px: 0.5, py: 0.5 }}>
                      <CustomTextField
                        value={item.totalPrice}
                        type="number"
                        onChange={(e) => {
                          const value = Number(e.target.value);
                          setInvoiceItems((items) =>
                            items.map((it, i) =>
                              i === idx ? { ...it, totalPrice: value } : it
                            )
                          );
                        }}
                        fullWidth
                        size="small"
                        sx={{ fontSize: 13, px: 0.5, py: 0.5 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CustomDialog>
        <ConfirmDialog
          open={confirmOpen}
          onClose={() => {
            setConfirmOpen(false);
            setToDeleteId(null);
          }}
          title="Delete entry"
          message="Are you sure you want to delete this entry? This action cannot be undone."
          onConfirm={async () => {
            if (toDeleteId === null) return;
            try {
              await deleteInvoice(String(toDeleteId));
              await fetchInvoices();
              setConfirmOpen(false);
              setToDeleteId(null);
            } catch (error) {
              console.error("Error deleting invoice:", error);
              setConfirmOpen(false);
              setToDeleteId(null);
            }
          }}
        />
      </Box>
    </Box>
  );
};

export default Invoices;
